<template>
  <panel class="mt-6 pt-6">
    <div class="flex flex-wrap -mx-2">
      <div class="flex items-stretch w-full md:w-1/2 lg:w-2/3 px-2">
        <GmapMap
          class="w-full h-120 mb-6 sm:mb-0 rounded overflow-hidden"
          :center="{lat: results.coordinates.lat, lng: results.coordinates.lon}"
          :zoom="9"
          :options="options">
          <GmapCircle
            v-if="results.data !== null"
            :center="{lat: results.coordinates.lat, lng: results.coordinates.lon}"
            :radius="24140.2"
            :options="{fillColor: 'white', strokeColor: 'navy'}"/>
          <GmapMarker
            v-if="results.data !== null"
            :position="{lat: results.coordinates.lat, lng: results.coordinates.lon}"
            :icon="{url: 'http://maps.google.com/mapfiles/kml/paddle/blu-circle.png', scaledSize: {width: 32, height: 32}}"
            :title="searchParams.q"/>
          <GmapMarker
            v-for="office in results.data"
            :key="office.id"
            :position="{lat: office.coordinates.lat, lng: office.coordinates.lon}"
            :icon="{url: activeOffice === office.id ? 'http://maps.google.com/mapfiles/kml/paddle/grn-circle.png' : 'http://maps.google.com/mapfiles/kml/paddle/wht-circle.png', scaledSize: {width: 32, height: 32}}"
            @click="activeOffice = office.id"
            :title="`${office.name} (${office.address_town})`"/>
        </GmapMap>
      </div>
      <div class="h-120 md:w-1/2 lg:w-1/3 px-2 flex flex-col">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full px-2 mb-6" :class="{'md:w-1/2': [7, 8].includes(selectedOffice.network_id)}">
            <label>Search</label>
            <LocationAutoComplete
              v-model="searchParams.q"
              :country="this.searchParams.country"
              @selected="addressSelected"
              @updated="addressSelected"
            />
          </div>

          <div class="w-full md:w-1/2 px-2 mb-6" v-if="[7, 8].includes(selectedOffice.network_id)">
            <label>Country</label>
            <select v-model="searchParams.country"
                    class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
              <option value="AU">Australia</option>
              <option value="BB">Barbados</option>
              <option value="CY">Cyprus</option>
              <option value="FR">France</option>
              <option value="DE">Germany</option>
              <option value="HU">Hungary</option>
              <option value="IE">Ireland</option>
              <option value="JE">Jersey</option>
              <option value="MU">Mauritius</option>
              <option value="NA">Namibia</option>
              <option value="PT">Portugal</option>
              <option value="ZA">South Africa</option>
              <option value="ES">Spain</option>
              <option value="CH">Switzerland</option>
              <option value="TH">Thailand</option>
              <option value="AE">United Arab Emirates</option>
              <option value="GB">United Kingdom</option>
              <option value="ZW">Zimbabwe</option>
            </select>
          </div>
        </div>
        <loader v-if="results.data === null"/>
        <div v-else-if="results.data.length === 0">
          <i class="fas fa-info-circle"></i> Your search returned no results.
        </div>
        <div v-else class="flex-grow overflow-y-auto rounded">
          <div v-for="office in results.data" :key="office.id"
               class="w-full rounded flex items-stretch mb-3 overflow-hidden border"
               :class="activeOffice === office.id ? 'bg-green-lightest border-green-dark' : 'bg-grey-lightest hover:bg-grey-lighter cursor-pointer border-grey-light'"
               @click="activeOffice = office.id">
            <div class="flex items-center justify-center p-2"
                 :class="activeOffice === office.id ? 'bg-green-dark text-white' : 'bg-grey-light'">
              <i class="fas fa-map-marker-alt fa-fw" :class="activeOffice === office.id ? '' : 'invisible'"></i>
            </div>
            <div class="flex-grow p-3">
              <strong class="block">{{ office.name }} ({{ office.address_town }})</strong>
              <small class="block mb-3 text-gray-600">{{
                  [office.address_name_number, office.address_line2, office.address_line3, office.address_town, office.address_line4, office.address_postcode].filter(Boolean).join(', ')
                }}, {{ office.country }}</small>
              <small class="block">{{ office.sales_phone }}</small>
              <small class="block mb-3">{{ office.sales_email }}</small>
              <small class="block">{{ office.distance.toFixed(2) }} miles away</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </panel>
</template>
<script>
import { v2Api } from '@/http'
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import LocationAutoComplete from '@/components/LocationAutoComplete'

export default {
  data () {
    return {
      options: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: false,
        disableDefaultUi: false
      },
      searchParams: {
        q: null,
        country: 'GB'
      },
      results: {
        data: null,
        coordinates: {
          lat: 51.5287718,
          lon: -0.2416813
        }
      },
      activeOffice: null
    }
  },
  created () {
    this.searchParams = {
      q: this.selectedOffice.address_town,
      country: this.selectedOffice.country
    }
  },
  computed: {
    ...mapState(['selectedOffice'])
  },
  methods: {
    search: debounce(function () {
      if (!this.searchParams.q) {
        return false
      }
      let url = '/office'
      let networkId = this.selectedOffice.network_id
      if (this.selectedOffice.network_id === 7 && this.searchParams.country === 'GB') {
        url = '/referrals/find-an-agent'
      }
      if (this.searchParams.country !== 'GB') {
        networkId = 8
      }
      v2Api.get(url, {
        params: {
          referral_network_id: networkId,
          referral_office_id: this.selectedOffice.id,
          ...this.searchParams,
          order_by: 'distance',
          limit_by_country: this.searchParams.country.length > 1 ? 1 : 0
        }
      }).then((response) => {
        this.results = response.data
      })
    }, 500),
    addressSelected (address) {
      // get nearby offices
      this.getNearbyAgents(address, 2)
    },
    getNearbyAgents (keyword, maxMile) {
      this.searchParams.q = keyword
      let networkId = (this.selectedOffice.network_id !== 7 || this.form.address_country === 'GB') ? this.selectedOffice.network_id : 8
      v2Api.get('office', {
        params: {
          referral_network_id: networkId,
          q: keyword,
          country: this.form['address_country'],
          order_by: 'distance',
          max_mile: maxMile,
          limit_by_country: 1
        }
      }).then((response) => {
        this.map.zoom = 10
        this.agentResults = response.data
        if (!this.agentResults || this.agentResults.data.length === 0) {
          // If no location match is made then the system displays all offices in that country along with the pins on a map.
          v2Api.get('office', {
            params: {
              referral_network_id: networkId,
              q: keyword,
              country: this.form['address_country'],
              order_by: 'distance',
              limit_by_country: 1
            }
          }).then((response) => {
            this.agentResults = response.data
            this.map.zoom = 5
          })
        }
      })
    }
  },
  watch: {
    searchParams: {
      handler () {
        this.search()
      },
      deep: true
    }
  },
  components: {
    LocationAutoComplete
  }
}
</script>
